// 智能缓存管理器 - 实现图片和内容缓存管理

import type { CacheType } from '@/types/reading';
import type { StorageManager } from './storage';

export interface CacheConfig {
  // 缓存大小限制 (MB)
  maxCacheSize: number;
  // 默认TTL (小时)
  defaultTTL: number;
  // 不同类型的TTL配置
  typeTTL: Record<CacheType, number>;
  // 清理阈值 (当缓存使用率超过此值时触发清理)
  cleanupThreshold: number;
  // 预加载配置
  preloadEnabled: boolean;
  // 压缩配置
  compressionEnabled: boolean;
}

export interface CacheStats {
  totalItems: number;
  totalSize: number;
  expiredItems: number;
  hitRate: number;
  typeStats: Record<CacheType, {
    count: number;
    size: number;
    hits: number;
    misses: number;
  }>;
}

export interface CacheItem<T = any> {
  key: string;
  type: CacheType;
  data: T;
  size: number;
  createdAt: Date;
  expiresAt: Date;
  lastAccessed: Date;
  accessCount: number;
  priority: number;
}

export class CacheManager {
  private storage: StorageManager;
  private config: CacheConfig;
  private stats: CacheStats;
  private cleanupTimer: NodeJS.Timeout | null = null;
  private preloadQueue: Set<string> = new Set();

  constructor(storage: StorageManager, config?: Partial<CacheConfig>) {
    this.storage = storage;
    this.config = {
      maxCacheSize: 100, // 100MB
      defaultTTL: 24, // 24小时
      typeTTL: {
        'chapter': 72, // 章节内容缓存3天
        'image': 168, // 图片缓存7天
        'search': 1, // 搜索结果缓存1小时
        'book_info': 24, // 图书信息缓存1天
        'category': 12, // 分类信息缓存12小时
        'user_data': 720 // 用户数据缓存30天
      },
      cleanupThreshold: 0.8, // 80%
      preloadEnabled: true,
      compressionEnabled: true,
      ...config
    };

    this.stats = this.initializeStats();
    this.startCleanupTimer();
  }

  // ============================================================================
  // 核心缓存操作
  // ============================================================================

  async set<T>(type: CacheType, key: string, data: T, options?: {
    ttl?: number;
    priority?: number;
    compress?: boolean;
  }): Promise<void> {
    try {
      const ttl = options?.ttl || this.config.typeTTL[type] || this.config.defaultTTL;
      const priority = options?.priority || this.calculatePriority(type, key);
      const compress = options?.compress ?? this.config.compressionEnabled;

      // 压缩数据（如果启用）
      const processedData = compress ? await this.compressData(data) : data;
      const size = this.calculateSize(processedData);

      // 检查缓存空间
      await this.ensureCacheSpace(size);

      // 存储到IndexedDB
      await this.storage.setCacheItem(type, key, {
        data: processedData,
        size,
        priority,
        compressed: compress,
        createdAt: new Date(),
        lastAccessed: new Date(),
        accessCount: 1
      }, ttl);

      // 更新统计
      this.updateStats(type, 'set', size);

      console.log(`Cache set: ${type}:${key} (${this.formatSize(size)})`);
    } catch (error) {
      console.error('Failed to set cache item:', error);
      throw error;
    }
  }

  async get<T>(type: CacheType, key: string): Promise<T | null> {
    try {
      const cacheItem = await this.storage.getCacheItem(type, key);
      
      if (!cacheItem) {
        this.updateStats(type, 'miss');
        return null;
      }

      // 更新访问统计
      await this.updateAccessStats(type, key, cacheItem);
      this.updateStats(type, 'hit');

      // 解压数据（如果需要）
      const data = cacheItem.compressed ? 
        await this.decompressData(cacheItem.data) : 
        cacheItem.data;

      console.log(`Cache hit: ${type}:${key}`);
      return data;
    } catch (error) {
      console.error('Failed to get cache item:', error);
      this.updateStats(type, 'miss');
      return null;
    }
  }

  async has(type: CacheType, key: string): Promise<boolean> {
    try {
      const item = await this.storage.getCacheItem(type, key);
      return item !== null;
    } catch (error) {
      console.error('Failed to check cache item:', error);
      return false;
    }
  }

  async delete(type: CacheType, key: string): Promise<void> {
    try {
      // 获取项目信息用于统计更新
      const item = await this.storage.getCacheItem(type, key);
      
      // 删除项目
      await this.storage.setCacheItem(type, key, null, 0); // 设置为null并立即过期
      
      // 更新统计
      if (item) {
        this.updateStats(type, 'delete', -item.size);
      }

      console.log(`Cache deleted: ${type}:${key}`);
    } catch (error) {
      console.error('Failed to delete cache item:', error);
      throw error;
    }
  }

  async clear(type?: CacheType): Promise<void> {
    try {
      if (type) {
        await this.storage.clearCacheByType(type);
        this.stats.typeStats[type] = {
          count: 0,
          size: 0,
          hits: 0,
          misses: 0
        };
      } else {
        // 清理所有缓存
        for (const cacheType of Object.keys(this.config.typeTTL) as CacheType[]) {
          await this.storage.clearCacheByType(cacheType);
        }
        this.stats = this.initializeStats();
      }

      console.log(`Cache cleared: ${type || 'all'}`);
    } catch (error) {
      console.error('Failed to clear cache:', error);
      throw error;
    }
  }

  // ============================================================================
  // 智能缓存策略
  // ============================================================================

  async ensureCacheSpace(requiredSize: number): Promise<void> {
    const storageInfo = await this.storage.getStorageInfo();
    const currentUsage = storageInfo.indexedDB.used;
    const maxSize = this.config.maxCacheSize * 1024 * 1024; // 转换为字节

    if (currentUsage + requiredSize > maxSize * this.config.cleanupThreshold) {
      await this.performIntelligentCleanup(requiredSize);
    }
  }

  private async performIntelligentCleanup(requiredSize: number): Promise<void> {
    console.log('Performing intelligent cache cleanup...');

    // 1. 首先清理过期项
    await this.storage.clearExpiredCache();

    // 2. 如果还需要更多空间，使用LRU策略清理
    const storageInfo = await this.storage.getStorageInfo();
    const currentUsage = storageInfo.indexedDB.used;
    const maxSize = this.config.maxCacheSize * 1024 * 1024;

    if (currentUsage + requiredSize > maxSize * this.config.cleanupThreshold) {
      await this.performLRUCleanup(requiredSize);
    }
  }

  private async performLRUCleanup(requiredSize: number): Promise<void> {
    // 获取所有缓存项并按访问时间和优先级排序
    const allItems = await this.getAllCacheItems();
    
    // 排序策略：优先级低的、访问次数少的、最久未访问的先被清理
    allItems.sort((a, b) => {
      // 优先级权重
      const priorityDiff = a.priority - b.priority;
      if (priorityDiff !== 0) return priorityDiff;

      // 访问频率权重
      const accessDiff = a.accessCount - b.accessCount;
      if (accessDiff !== 0) return accessDiff;

      // 最后访问时间权重
      return a.lastAccessed.getTime() - b.lastAccessed.getTime();
    });

    let freedSpace = 0;
    const itemsToDelete: Array<{ type: CacheType; key: string }> = [];

    for (const item of allItems) {
      if (freedSpace >= requiredSize) break;
      
      itemsToDelete.push({ type: item.type, key: item.key });
      freedSpace += item.size;
    }

    // 执行删除
    for (const { type, key } of itemsToDelete) {
      await this.delete(type, key);
    }

    console.log(`LRU cleanup completed: freed ${this.formatSize(freedSpace)} by removing ${itemsToDelete.length} items`);
  }

  // ============================================================================
  // 预加载功能
  // ============================================================================

  async preloadChapters(bookId: string, currentChapterIndex: number, count: number = 3): Promise<void> {
    if (!this.config.preloadEnabled) return;

    try {
      // 预加载当前章节前后的章节
      const preloadPromises: Promise<void>[] = [];

      for (let i = 1; i <= count; i++) {
        // 预加载下一章节
        const nextIndex = currentChapterIndex + i;
        const nextKey = `${bookId}:${nextIndex}`;
        
        if (!this.preloadQueue.has(nextKey)) {
          this.preloadQueue.add(nextKey);
          preloadPromises.push(this.preloadChapter(bookId, nextIndex));
        }

        // 预加载上一章节
        if (i <= 2) { // 只预加载前2章
          const prevIndex = currentChapterIndex - i;
          const prevKey = `${bookId}:${prevIndex}`;
          
          if (prevIndex >= 0 && !this.preloadQueue.has(prevKey)) {
            this.preloadQueue.add(prevKey);
            preloadPromises.push(this.preloadChapter(bookId, prevIndex));
          }
        }
      }

      await Promise.allSettled(preloadPromises);
    } catch (error) {
      console.error('Failed to preload chapters:', error);
    }
  }

  private async preloadChapter(bookId: string, chapterIndex: number): Promise<void> {
    try {
      const key = `${bookId}:${chapterIndex}`;
      
      // 检查是否已经缓存
      if (await this.has('chapter', key)) {
        this.preloadQueue.delete(key);
        return;
      }

      // 这里应该调用实际的章节获取服务
      // 为了示例，我们模拟一个延迟
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // 模拟章节内容
      const chapterContent = `Chapter ${chapterIndex} content for book ${bookId}`;
      
      await this.set('chapter', key, chapterContent, {
        priority: 5, // 预加载内容优先级较低
        ttl: this.config.typeTTL.chapter
      });

      this.preloadQueue.delete(key);
      console.log(`Preloaded chapter: ${key}`);
    } catch (error) {
      console.error(`Failed to preload chapter ${bookId}:${chapterIndex}:`, error);
      this.preloadQueue.delete(`${bookId}:${chapterIndex}`);
    }
  }

  async preloadImages(imageUrls: string[]): Promise<void> {
    if (!this.config.preloadEnabled) return;

    const preloadPromises = imageUrls.map(async (url) => {
      try {
        if (await this.has('image', url)) return;

        // 预加载图片
        const response = await fetch(url);
        if (response.ok) {
          const blob = await response.blob();
          const arrayBuffer = await blob.arrayBuffer();
          
          await this.set('image', url, arrayBuffer, {
            priority: 3, // 图片预加载优先级中等
            ttl: this.config.typeTTL.image
          });

          console.log(`Preloaded image: ${url}`);
        }
      } catch (error) {
        console.error(`Failed to preload image ${url}:`, error);
      }
    });

    await Promise.allSettled(preloadPromises);
  }

  // ============================================================================
  // 缓存统计和监控
  // ============================================================================

  async getStats(): Promise<CacheStats> {
    try {
      // 更新实时统计
      const storageInfo = await this.storage.getStorageInfo();
      
      this.stats.totalItems = storageInfo.cache.itemCount;
      this.stats.totalSize = storageInfo.cache.totalSize;
      this.stats.expiredItems = storageInfo.cache.expiredCount;

      return { ...this.stats };
    } catch (error) {
      console.error('Failed to get cache stats:', error);
      return this.stats;
    }
  }

  async getCacheHealth(): Promise<{
    status: 'healthy' | 'warning' | 'critical';
    issues: string[];
    recommendations: string[];
  }> {
    const stats = await this.getStats();
    const storageInfo = await this.storage.getStorageInfo();
    const usageRatio = storageInfo.indexedDB.used / (this.config.maxCacheSize * 1024 * 1024);
    
    const issues: string[] = [];
    const recommendations: string[] = [];
    let status: 'healthy' | 'warning' | 'critical' = 'healthy';

    // 检查缓存使用率
    if (usageRatio > 0.9) {
      status = 'critical';
      issues.push('Cache usage is critically high (>90%)');
      recommendations.push('Consider increasing cache size or reducing TTL values');
    } else if (usageRatio > 0.8) {
      status = 'warning';
      issues.push('Cache usage is high (>80%)');
      recommendations.push('Monitor cache usage and consider cleanup');
    }

    // 检查过期项比例
    const expiredRatio = stats.expiredItems / stats.totalItems;
    if (expiredRatio > 0.3) {
      if (status !== 'critical') status = 'warning';
      issues.push('High number of expired items (>30%)');
      recommendations.push('Run cache cleanup more frequently');
    }

    // 检查命中率
    if (stats.hitRate < 0.5) {
      if (status !== 'critical') status = 'warning';
      issues.push('Low cache hit rate (<50%)');
      recommendations.push('Review caching strategy and TTL values');
    }

    return { status, issues, recommendations };
  }

  // ============================================================================
  // 数据压缩和解压
  // ============================================================================

  private async compressData(data: any): Promise<string> {
    try {
      const jsonString = JSON.stringify(data);
      
      // 使用简单的压缩算法（在实际应用中可以使用更好的压缩库）
      if (jsonString.length > 1000) {
        // 对于大数据使用压缩
        const compressed = this.simpleCompress(jsonString);
        return compressed;
      }
      
      return jsonString;
    } catch (error) {
      console.error('Failed to compress data:', error);
      return JSON.stringify(data);
    }
  }

  private async decompressData(compressedData: string): Promise<any> {
    try {
      // 检查是否是压缩数据
      if (compressedData.startsWith('COMPRESSED:')) {
        const decompressed = this.simpleDecompress(compressedData);
        return JSON.parse(decompressed);
      }
      
      return JSON.parse(compressedData);
    } catch (error) {
      console.error('Failed to decompress data:', error);
      return null;
    }
  }

  private simpleCompress(data: string): string {
    // 简单的RLE压缩实现
    let compressed = 'COMPRESSED:';
    let count = 1;
    let current = data[0];
    
    for (let i = 1; i < data.length; i++) {
      if (data[i] === current && count < 255) {
        count++;
      } else {
        compressed += count > 1 ? `${count}${current}` : current;
        current = data[i];
        count = 1;
      }
    }
    
    compressed += count > 1 ? `${count}${current}` : current;
    return compressed;
  }

  private simpleDecompress(compressedData: string): string {
    const data = compressedData.replace('COMPRESSED:', '');
    let decompressed = '';
    let i = 0;
    
    while (i < data.length) {
      if (/\d/.test(data[i])) {
        const count = parseInt(data[i]);
        const char = data[i + 1];
        decompressed += char.repeat(count);
        i += 2;
      } else {
        decompressed += data[i];
        i++;
      }
    }
    
    return decompressed;
  }

  // ============================================================================
  // 辅助方法
  // ============================================================================

  private initializeStats(): CacheStats {
    const typeStats: Record<CacheType, any> = {
      'chapter': { count: 0, size: 0, hits: 0, misses: 0 },
      'image': { count: 0, size: 0, hits: 0, misses: 0 },
      'search': { count: 0, size: 0, hits: 0, misses: 0 },
      'book_info': { count: 0, size: 0, hits: 0, misses: 0 },
      'category': { count: 0, size: 0, hits: 0, misses: 0 },
      'user_data': { count: 0, size: 0, hits: 0, misses: 0 }
    };

    return {
      totalItems: 0,
      totalSize: 0,
      expiredItems: 0,
      hitRate: 0,
      typeStats
    };
  }

  private updateStats(type: CacheType, operation: 'set' | 'hit' | 'miss' | 'delete', sizeChange: number = 0): void {
    const typeStats = this.stats.typeStats[type];
    
    switch (operation) {
      case 'set':
        typeStats.count++;
        typeStats.size += sizeChange;
        this.stats.totalItems++;
        this.stats.totalSize += sizeChange;
        break;
      case 'hit':
        typeStats.hits++;
        break;
      case 'miss':
        typeStats.misses++;
        break;
      case 'delete':
        typeStats.count--;
        typeStats.size += sizeChange; // sizeChange is negative
        this.stats.totalItems--;
        this.stats.totalSize += sizeChange;
        break;
    }

    // 更新命中率
    const totalHits = Object.values(this.stats.typeStats).reduce((sum, stats) => sum + stats.hits, 0);
    const totalMisses = Object.values(this.stats.typeStats).reduce((sum, stats) => sum + stats.misses, 0);
    this.stats.hitRate = totalHits / (totalHits + totalMisses) || 0;
  }

  private calculateSize(data: any): number {
    return JSON.stringify(data).length * 2; // 估算字节大小
  }

  private calculatePriority(type: CacheType, _key: string): number {
    // 根据类型和键计算优先级
    const typePriority: Record<CacheType, number> = {
      'chapter': 10, // 章节内容最高优先级
      'book_info': 8, // 图书信息高优先级
      'image': 6, // 图片中等优先级
      'category': 5, // 分类信息中等优先级
      'search': 3, // 搜索结果低优先级
      'user_data': 9 // 用户数据高优先级
    };

    return typePriority[type] || 5;
  }

  private async updateAccessStats(type: CacheType, key: string, cacheItem: any): Promise<void> {
    try {
      const updatedItem = {
        ...cacheItem,
        lastAccessed: new Date(),
        accessCount: (cacheItem.accessCount || 0) + 1
      };

      await this.storage.setCacheItem(type, key, updatedItem, this.config.typeTTL[type]);
    } catch (error) {
      console.error('Failed to update access stats:', error);
    }
  }

  private async getAllCacheItems(): Promise<CacheItem[]> {
    // 这里需要实现获取所有缓存项的逻辑
    // 由于IndexedDB的限制，这里返回空数组作为占位符
    return [];
  }

  private formatSize(bytes: number): string {
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(2)} ${units[unitIndex]}`;
  }

  private startCleanupTimer(): void {
    // 每小时执行一次清理
    this.cleanupTimer = setInterval(async () => {
      try {
        await this.storage.clearExpiredCache();
        console.log('Scheduled cache cleanup completed');
      } catch (error) {
        console.error('Scheduled cache cleanup failed:', error);
      }
    }, 60 * 60 * 1000);
  }

  // ============================================================================
  // 清理资源
  // ============================================================================

  destroy(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
    
    this.preloadQueue.clear();
    console.log('Cache manager destroyed');
  }
}

// 全局缓存管理器实例
export let globalCacheManager: CacheManager | null = null;

export function initializeCacheManager(storage: StorageManager, config?: Partial<CacheConfig>): CacheManager {
  if (globalCacheManager) {
    globalCacheManager.destroy();
  }
  
  globalCacheManager = new CacheManager(storage, config);
  return globalCacheManager;
}